import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { currency, Stars, StatusBadge } from "@/components/Bits";
import ShareButton from "@/components/ShareButton";
import { MapPin, Clock, Calendar, DollarSign, Send, Check, X, CheckCircle, Star, AlertTriangle, HandCoins, Bookmark, BookmarkCheck, ThumbsDown } from "lucide-react";

export default function ShiftDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [shift, setShift] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [offer, setOffer] = useState("");
  const [offerMsg, setOfferMsg] = useState("");
  const [rateModal, setRateModal] = useState(false);
  const [acceptModal, setAcceptModal] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [ratings, setRatings] = useState([]);
  const [hasRated, setHasRated] = useState(false);
  const msgBox = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/shifts/${id}`);
      setShift(data);
    } catch (e) {
      toast.error("Diária não encontrada");
      nav("/shifts");
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/shifts/${id}/messages`);
      // Keep any locally-pending messages until server catches up
      setMessages(prev => {
        const pending = prev.filter(m => m._pending);
        const serverIds = new Set((data.messages || []).map(m => m.message_id));
        const stillPending = pending.filter(p => !serverIds.has(p.message_id));
        return [...(data.messages || []), ...stillPending];
      });
    } catch {}
  };

  const loadRatings = async () => {
    try {
      const { data } = await api.get(`/shifts/${id}/ratings`);
      setRatings(data.ratings);
      if (data.ratings.some(r => r.rater_id === user?.user_id)) setHasRated(true);
    } catch {}
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!shift) return;
    loadMessages();
    if (shift.status === "completed") loadRatings();
    const iv = setInterval(loadMessages, 4000);
    return () => clearInterval(iv);
  }, [shift?.shift_id, shift?.status]);
  useEffect(() => { msgBox.current?.scrollTo({top: 99999, behavior: "smooth"}); }, [messages]);

  if (!shift) return <div className="text-zinc-400">Carregando…</div>;

  const isCommerce = user?.role === "comercio" && user?.user_id === shift.commerce_id;
  const isMyShift = user?.role === "acougueiro" && user?.user_id === shift.worker_id;
  const canOffer = user?.role === "acougueiro" && ["open", "negotiating"].includes(shift.status) && !isMyShift;
  const canAccept = user?.role === "acougueiro" && ["open", "negotiating"].includes(shift.status) && !isMyShift;

  const doAccept = async () => {
    setAccepting(true);
    try { await api.post(`/shifts/${id}/accept`); toast.success("Diária aceita!"); setAcceptModal(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAccepting(false); }
  };
  const doCounterOffer = async () => {
    if (!offer) return;
    try {
      await api.post(`/shifts/${id}/counter-offer`, { proposed_rate: parseFloat(offer), message: offerMsg });
      toast.success("Contra-oferta enviada");
      setOffer(""); setOfferMsg(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const acceptOffer = async (offer_id) => {
    try { await api.post(`/shifts/${id}/offer/${offer_id}/accept`); toast.success("Oferta aceita!"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const complete = async () => {
    try { await api.post(`/shifts/${id}/complete`); toast.success("Pagamento confirmado! Diária finalizada."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sign = async () => {
    try { await api.post(`/shifts/${id}/sign`); toast.success("Contrato assinado digitalmente"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cancel = async () => {
    if (!confirm("Deseja realmente cancelar?")) return;
    try { await api.post(`/shifts/${id}/cancel`); toast.success("Cancelado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveForLater = async () => {
    try {
      await api.post(`/shifts/${id}/${shift?.is_saved ? "unsave" : "save"}`);
      toast.success(shift?.is_saved ? "Removida da lista" : "Salva para decidir depois");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dismiss = async () => {
    if (!confirm("Recusar essa diária? Ela some do seu feed.")) return;
    try {
      await api.post(`/shifts/${id}/dismiss`);
      toast.success("Diária recusada");
      nav("/shifts");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendMsg = async () => {
    if (!msgInput.trim()) return;
    const content = msgInput;
    setMsgInput("");
    // Optimistic UI: show message immediately
    const optimistic = {
      message_id: `local_${Date.now()}`,
      shift_id: id,
      sender_id: user?.user_id,
      sender_name: user?.name || "Você",
      sender_role: user?.role,
      content,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages(prev => [...prev, optimistic]);
    try {
      await api.post(`/shifts/${id}/messages`, { content });
      loadMessages();
    } catch (err) {
      // Rollback optimistic
      setMessages(prev => prev.filter(m => m.message_id !== optimistic.message_id));
      setMsgInput(content);
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao enviar mensagem");
    }
  };
  const rate = async () => {
    try {
      await api.post(`/shifts/${id}/rate`, { stars, comment });
      toast.success("Avaliação enviada!");
      setRateModal(false); loadRatings();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6 anim-fade-up">
      <div className="lg:col-span-2 space-y-4">
        <div className="cw-card p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{shift.commerce_name}</div>
              <h1 className="text-2xl sm:text-3xl font-display font-extrabold">{shift.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <ShareButton
                variant="icon"
                testid="btn-share-shift"
                title={`Diária em ${shift.city || shift.commerce_name}`}
                text={`Vaga aberta: ${shift.title} · ${shift.city || ""} · R$ ${Number(shift.final_rate || 0).toFixed(2)}. Conhece alguém?`}
              />
              <StatusBadge status={shift.status} />
            </div>
          </div>
          <p className="text-zinc-300 mb-6">{shift.description || "Sem descrição."}</p>

          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <InfoRow icon={Calendar} label="Data" value={shift.date} />
            <InfoRow icon={Clock} label="Horário" value={`${shift.start_time} – ${shift.end_time}`} />
            <InfoRow icon={MapPin} label="Local" value={shift.address ? `${shift.address}, ${shift.city}` : shift.city} />
            <InfoRow icon={DollarSign} label="Valor" value={<span className="text-red-500 font-bold text-lg">{currency(shift.final_rate)}</span>} />
          </div>

          {shift.worker_name && (
            <div className="mt-4 pt-4 border-t border-zinc-800 text-sm flex items-center justify-between gap-2">
              <div>
                <span className="text-zinc-500">Profissional aceito: </span>
                <span className="text-white font-semibold">{shift.worker_name}</span>
              </div>
              {isCommerce && shift.worker_id && (
                <button onClick={async () => {
                  try {
                    const { data } = await api.post("/conversations", { other_user_id: shift.worker_id });
                    nav(`/messages?id=${data.conversation_id}`);
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }} className="cw-btn-outline text-xs !py-1.5 !px-3 inline-flex items-center gap-1" data-testid="btn-dm-worker">
                  <Send className="w-3 h-3" /> Mensagem
                </button>
              )}
              {isMyShift && (
                <button onClick={async () => {
                  try {
                    const { data } = await api.post("/conversations", { other_user_id: shift.commerce_id });
                    nav(`/messages?id=${data.conversation_id}`);
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }} className="cw-btn-outline text-xs !py-1.5 !px-3 inline-flex items-center gap-1" data-testid="btn-dm-commerce">
                  <Send className="w-3 h-3" /> Mensagem
                </button>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            {canAccept && (
              <>
                <button onClick={() => setAcceptModal(true)} className="cw-btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-accept-shift">
                  <CheckCircle className="w-4 h-4" /> Aceitar diária
                </button>
                <button onClick={saveForLater} className="cw-btn-outline text-sm inline-flex items-center gap-2" data-testid="btn-save-shift">
                  {shift.is_saved ? <><BookmarkCheck className="w-4 h-4 text-amber-400" /> Salva</> : <><Bookmark className="w-4 h-4" /> Pendente</>}
                </button>
                <button onClick={dismiss} className="cw-btn-outline text-sm inline-flex items-center gap-2 text-red-400" data-testid="btn-dismiss-shift">
                  <ThumbsDown className="w-4 h-4" /> Recusar
                </button>
              </>
            )}
            {isCommerce && shift.status === "accepted" && (
          <button onClick={complete} className="cw-btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-complete">
            <CheckCircle className="w-4 h-4" /> Finalizar & confirmar pagamento
          </button>
        )}
        {isCommerce && shift.status === "in_progress" && (
          <button onClick={complete} className="cw-btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-complete-inprogress">
            <CheckCircle className="w-4 h-4" /> Finalizar & confirmar pagamento
          </button>
        )}
            {(isCommerce || isMyShift) && !["completed", "cancelled"].includes(shift.status) && (
              <button onClick={cancel} className="cw-btn-outline text-sm text-red-400" data-testid="btn-cancel">Cancelar</button>
            )}
            {shift.status === "completed" && (isCommerce || isMyShift) && !hasRated && (
              <button onClick={() => setRateModal(true)} className="cw-btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-open-rate">
                <Star className="w-4 h-4" /> Avaliar
              </button>
            )}
          </div>

          {/* Digital Contract */}
          {["accepted", "in_progress"].includes(shift.status) && (isCommerce || isMyShift) && (
            <div className="mt-6 pt-6 border-t border-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">📝</span>
                <h3 className="font-display font-bold">Contrato Digital</h3>
              </div>
              <div className="bg-zinc-950/50 rounded-lg p-4 text-xs text-zinc-300 mb-3 max-h-40 overflow-y-auto leading-relaxed">
                <p><b>TERMO DE PRESTAÇÃO DE DIÁRIA</b></p>
                <p className="mt-2">O comércio <b>{shift.commerce_name}</b> contrata o profissional <b>{shift.worker_name || "—"}</b> para prestar serviço no dia <b>{shift.date}</b>, das <b>{shift.start_time}</b> às <b>{shift.end_time}</b>, no endereço <b>{shift.address}, {shift.city}</b>, pelo valor combinado de <b>R$ {shift.final_rate?.toFixed(2)}</b>.</p>
                <p className="mt-2">O pagamento será realizado pelo comércio diretamente ao profissional ao fim do expediente. Este acordo não gera vínculo empregatício. Ambas as partes se comprometem com pontualidade, respeito mútuo e sigilo sobre informações do estabelecimento.</p>
                <p className="mt-2">Ao clicar em "Assinar", ambas as partes confirmam ter lido e aceito estes termos. A assinatura é registrada com data, hora e IP e vale como prova em caso de disputa.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className={`p-3 rounded-lg border ${shift.commerce_signed_at ? "border-emerald-700 bg-emerald-950/30" : "border-zinc-800"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Comércio</div>
                  <div className="text-sm font-semibold">{shift.commerce_name}</div>
                  {shift.commerce_signed_at ? (
                    <div className="text-xs text-emerald-400 mt-1 font-mono">✓ Assinado {shift.commerce_signed_at.slice(0,16).replace("T"," ")}</div>
                  ) : isCommerce ? (
                    <button onClick={sign} className="cw-btn-primary text-xs mt-2 !py-1.5 !px-3" data-testid="btn-sign-commerce">Assinar como Comércio</button>
                  ) : <div className="text-xs text-zinc-500 mt-1">Aguardando…</div>}
                </div>
                <div className={`p-3 rounded-lg border ${shift.worker_signed_at ? "border-emerald-700 bg-emerald-950/30" : "border-zinc-800"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Profissional</div>
                  <div className="text-sm font-semibold">{shift.worker_name || "—"}</div>
                  {shift.worker_signed_at ? (
                    <div className="text-xs text-emerald-400 mt-1 font-mono">✓ Assinado {shift.worker_signed_at.slice(0,16).replace("T"," ")}</div>
                  ) : isMyShift ? (
                    <button onClick={sign} className="cw-btn-primary text-xs mt-2 !py-1.5 !px-3" data-testid="btn-sign-worker">Assinar como Profissional</button>
                  ) : <div className="text-xs text-zinc-500 mt-1">Aguardando…</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Counter offers */}
        {shift.counter_offers?.length > 0 && (
          <div className="cw-card p-6">
            <h3 className="font-display font-bold mb-3">Ofertas recebidas</h3>
            <div className="space-y-2">
              {shift.counter_offers.map(o => (
                <div key={o.offer_id} className="flex items-center justify-between border border-zinc-800 rounded-lg p-3">
                  <div>
                    <div className="font-semibold">{o.worker_name}</div>
                    <div className="text-xs text-zinc-400">"{o.message || "—"}"</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-bold text-red-500 font-mono">{currency(o.proposed_rate)}</div>
                    <span className={`badge-pill status-${o.status === "pending" ? "negotiating" : o.status === "accepted" ? "completed" : "cancelled"}`}>
                      {o.status === "pending" ? "Pendente" : o.status === "accepted" ? "Aceita" : "Rejeitada"}
                    </span>
                    {isCommerce && o.status === "pending" && (
                      <button onClick={() => acceptOffer(o.offer_id)} className="cw-btn-primary text-xs !py-1 !px-2" data-testid={`btn-accept-offer-${o.offer_id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Counter offer form */}
        {canOffer && (
          <div className="cw-card p-6">
            <h3 className="font-display font-bold mb-3">Fazer contra-oferta</h3>
            <div className="flex gap-2">
              <input type="number" value={offer} onChange={e => setOffer(e.target.value)}
                     placeholder="Valor proposto" className="cw-input" data-testid="input-offer-rate" />
              <input value={offerMsg} onChange={e => setOfferMsg(e.target.value)}
                     placeholder="Mensagem (opcional)" className="cw-input" data-testid="input-offer-msg" />
              <button onClick={doCounterOffer} className="cw-btn-primary" data-testid="btn-send-offer">Enviar</button>
            </div>
          </div>
        )}

        {/* Ratings */}
        {ratings.length > 0 && (
          <div className="cw-card p-6">
            <h3 className="font-display font-bold mb-3">Avaliações</h3>
            <div className="space-y-3">
              {ratings.map(r => (
                <div key={r.rating_id} className="border-b border-zinc-800 pb-2 last:border-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold">{r.rater_name}</span>
                    <Stars value={r.stars} />
                  </div>
                  <div className="text-sm text-zinc-400">{r.comment || "Sem comentário"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chat sidebar */}
      <div className="cw-card p-0 flex flex-col h-[600px]">
        <div className="p-4 border-b border-zinc-800 font-semibold">Chat da diária</div>
        <div ref={msgBox} className="flex-1 overflow-y-auto p-4 space-y-2">
          {messages.length === 0 ? (
            <div className="text-center text-zinc-500 text-sm mt-8">Nenhuma mensagem ainda.</div>
          ) : messages.map(m => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-100"}`}>
                  {!mine && <div className="text-[10px] text-zinc-400 mb-0.5">{m.sender_name}</div>}
                  {m.content}
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-zinc-800 flex gap-2">
          <input value={msgInput} onChange={e => setMsgInput(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && sendMsg()}
                 placeholder="Digite..." className="cw-input" data-testid="input-chat" />
          <button onClick={sendMsg} className="cw-btn-primary !px-3" data-testid="btn-chat-send"><Send className="w-4 h-4" /></button>
        </div>
      </div>

      {acceptModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !accepting && setAcceptModal(false)}>
          <div className="cw-card p-6 max-w-md w-full anim-fade-up" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <HandCoins className="w-6 h-6 text-red-500" />
                <h3 className="font-display font-bold text-xl">Confirmar aceite</h3>
              </div>
              <button onClick={() => !accepting && setAcceptModal(false)} data-testid="btn-close-accept"><X className="w-5 h-5 text-zinc-400" /></button>
            </div>

            <p className="text-sm text-zinc-300 mb-4">
              Você está aceitando a diária <b className="text-white">{shift.title}</b> em <b className="text-white">{shift.commerce_name}</b>.
            </p>

            <div className="bg-zinc-950/60 rounded-xl p-4 mb-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Data</span><span className="font-semibold">{shift.date}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Horário</span><span className="font-semibold">{shift.start_time} – {shift.end_time}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Cidade</span><span className="font-semibold">{shift.city}</span></div>
              <div className="flex justify-between items-baseline pt-2 border-t border-zinc-800">
                <span className="text-zinc-500 text-xs uppercase tracking-wider">Valor da diária</span>
                <span className="text-3xl font-display font-extrabold text-red-500">{currency(shift.final_rate)}</span>
              </div>
            </div>

            <div className="flex gap-2 items-start bg-amber-950/30 border border-amber-800 rounded-lg p-3 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200 leading-relaxed">
                Ao aceitar, você se compromete a comparecer. Cancelar depois <b>zera sua sequência de fidelidade</b> e desconta 10 pontos.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setAcceptModal(false)} disabled={accepting}
                      className="cw-btn-outline text-sm" data-testid="btn-cancel-accept">
                Cancelar
              </button>
              <button onClick={doAccept} disabled={accepting}
                      className="cw-btn-primary text-sm inline-flex items-center justify-center gap-2" data-testid="btn-confirm-accept">
                {accepting ? "Aceitando…" : <>Confirmar aceite <CheckCircle className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {rateModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRateModal(false)}>
          <div className="cw-card p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display font-bold text-xl">Avaliar</h3>
              <button onClick={() => setRateModal(false)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <button key={i} onClick={() => setStars(i)} data-testid={`btn-star-${i}`}>
                  <Star className={`w-8 h-8 ${i <= stars ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                </button>
              ))}
            </div>
            <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)}
                      placeholder="Deixe um comentário (opcional)"
                      className="cw-input" data-testid="input-rate-comment" />
            <button onClick={rate} className="cw-btn-primary w-full mt-3" data-testid="btn-submit-rate">Enviar avaliação</button>
          </div>
        </div>
      )}
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-red-500 mt-0.5" />
    <div>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="text-zinc-200">{value}</div>
    </div>
  </div>
);
